import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Container, Row, Col } from 'react-bootstrap'
import { m } from 'framer-motion'

// Components
import PostCard from '../../Components/Imigo/PostCard'
import NationalityFilter from '../../Components/Imigo/NationalityFilter'
import SEO from '../../Components/SEO/SEO'
import { generateOrganizationSchema, generateWebSiteSchema } from '../../Components/SEO/StructuredData'
import Universe3D from '../../Components/Imigo/Universe3D'
import AnimatedCard from '../../Components/Imigo/AnimatedCard'
import AnimatedCounter from '../../Components/Imigo/AnimatedCounter'

// Context
import { useLanguage } from '../../Context/LanguageContext'

// Functions
import { fetchPosts, fetchPostsByType, fetchNationalitiesWithPosts } from '../../Functions/apiService'
import { getExcerpt, getImageUrl } from '../../Functions/Utilities'
import { fadeIn, fadeInLeft, fadeInRight, zoomIn } from '../../Functions/GlobalAnimations'

// Assets
import { WEB_BASE_URL } from '../../Functions/imigoWebConfig'
import appScreenshot from '../../Assets/img/imigo-app-screenshot.png'
import appleStore from '../../Assets/img/app-store-badge.png'
import googlePlay from '../../Assets/img/google-play-badge.png'

const HomePage = () => {
  const { t, getLocalizedPath } = useLanguage()
  const [articles, setArticles] = useState([])
  const [businesses, setBusinesses] = useState([])
  const [events, setEvents] = useState([])
  const [marketplace, setMarketplace] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [nationalities, setNationalities] = useState([])
  const [selectedNationality, setSelectedNationality] = useState(null)

  // Fetch nationalities that have posts on mount
  useEffect(() => {
    const fetchNationalitiesData = async () => {
      try {
        const response = await fetchNationalitiesWithPosts()
        setNationalities(response.data || [])
      } catch (err) {
        console.error('Error fetching nationalities:', err)
      }
    }
    
    fetchNationalitiesData()
  }, [])

  useEffect(() => {
    const fetchAllPostTypes = async () => {
      try {
        setLoading(true)
        
        // Build filters object
        const filters = {}
        if (selectedNationality) {
          filters['filters[nationality][id][$eq]'] = selectedNationality
        }
        
        // Fetch latest articles
        const articlesData = await fetchPostsByType('Article', { limit: 4, filters })
        setArticles(articlesData.data || [])
        
        // Fetch businesses
        const businessesData = await fetchPostsByType('Business', { limit: 4, filters })
        setBusinesses(businessesData.data || [])
        
        // Fetch events
        const eventsData = await fetchPostsByType('Event', { limit: 4, filters })
        setEvents(eventsData.data || [])
        
        // Fetch marketplace items
        const marketplaceData = await fetchPostsByType('Marketplace', { limit: 4, filters })
        setMarketplace(marketplaceData.data || [])
        
        setLoading(false)
      } catch (err) {
        console.error('Error fetching posts:', err)
        setError(t('home.error'))
        setLoading(false)
      }
    }
    
    fetchAllPostTypes()
  }, [selectedNationality, t])

  // Post type sections configuration
  const postTypeSections = [
    {
      title: t('home.sections.latestArticles.title'),
      description: t('home.sections.latestArticles.description'),
      type: 'Article',
      data: articles,
      link: getLocalizedPath('/articles'),
      color: 'bg-gradient-to-r from-purple-600 to-indigo-600'
    },
    {
      title: t('home.sections.localBusinesses.title'),
      description: t('home.sections.localBusinesses.description'),
      type: 'Business',
      data: businesses,
      link: getLocalizedPath('/businesses'),
      color: 'bg-gradient-to-r from-blue-500 to-cyan-500'
    },
    {
      title: t('home.sections.upcomingEvents.title'),
      description: t('home.sections.upcomingEvents.description'),
      type: 'Event',
      data: events,
      link: getLocalizedPath('/events'),
      color: 'bg-gradient-to-r from-orange-500 to-amber-500'
    },
    {
      title: t('home.sections.marketplace.title'),
      description: t('home.sections.marketplace.description'),
      type: 'Marketplace',
      data: marketplace,
      link: getLocalizedPath('/marketplace'),
      color: 'bg-gradient-to-r from-green-500 to-emerald-500'
    }
  ]

  // Generate structured data for homepage
  const organizationSchema = generateOrganizationSchema({
    name: 'Imigo',
    url: WEB_BASE_URL,
    description: 'Imigo is a versatile app designed for immigrants and tourists, featuring a modern interface and extensive functionality.',
  })

  const websiteSchema = generateWebSiteSchema({
    name: 'Imigo',
    url: WEB_BASE_URL,
    description: 'Imigo is a versatile app designed for immigrants and tourists, featuring a modern interface and extensive functionality.',
  })

  // Combine structured data
  const structuredData = [organizationSchema, websiteSchema]

  return (
    <div className="imigo-home overflow-hidden">
      <SEO
        image="/assets/pic/HomePic.jpg"
        structuredData={structuredData}
      />
      
      {/* Hero Section with 3D Universe */}
      <section className="hero-section relative min-h-[90vh] flex items-center overflow-hidden bg-black">
        {/* 3D Universe Background */}
        <div className="absolute inset-0 z-0">
          <Universe3D className="w-full h-full" />
        </div>
        
        {/* Overlay gradient for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60 z-[1]"></div>
        
        <Container className="relative z-10">
          <Row className="items-center min-h-[80vh]">
            <Col lg={6} md={12} className="text-white relative z-10">
              <m.div {...fadeInLeft}>
                <m.span 
                  className="inline-block text-sm font-semibold uppercase tracking-wider text-neonorange mb-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  Your Migration Journey Starts Here
                </m.span>
                <m.h1 
                  className="text-6xl md:text-5xl sm:text-4xl font-bold mb-6 leading-tight"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <span className="bg-gradient-to-r from-white via-fastblue to-neonorange bg-clip-text text-transparent">
                    {t('home.hero.title')}
                  </span>
                </m.h1>
                <m.p 
                  className="text-xl md:text-lg mb-8 text-gray-300 leading-relaxed"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  {t('home.hero.subtitle')}
                </m.p>
                <m.div 
                  className="app-download-buttons flex flex-wrap gap-4"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                >
                  <m.a 
                    href="https://apps.apple.com/us/app/imigo-connect/id6744267446" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <img src={appleStore} alt={t('common.onAppStore')} className="h-[60px] md:h-[50px] drop-shadow-lg" />
                  </m.a>
                  <m.a 
                    href="https://play.google.com/store/apps/details?id=app.imigo" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <img src={googlePlay} alt={t('common.onGooglePlay')} className="h-[60px] md:h-[50px] drop-shadow-lg" />
                  </m.a>
                </m.div>
              </m.div>
            </Col>
            <Col lg={6} md={12} className="mt-10 lg:mt-0 relative">
              <m.div 
                className="relative h-[500px] md:h-[400px] sm:h-[300px]"
                {...fadeInRight}
              >
                {/* App Screenshot Overlay */}
                <m.div
                  className="absolute bottom-0 right-0 md:right-auto md:left-1/2 md:-translate-x-1/2 z-10"
                  initial={{ opacity: 0, scale: 0.8, y: 50 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                  whileHover={{ scale: 1.05, y: -10 }}
                >
                  <img 
                    src={appScreenshot} 
                    alt="Imigo App" 
                    className="max-w-[250px] md:max-w-[200px] drop-shadow-2xl rounded-2xl border-4 border-white/20" 
                  />
                </m.div>
              </m.div>
            </Col>
          </Row>
        </Container>
        
        {/* Scroll Indicator */}
        <m.div 
          className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, delay: 1 }}
        >
          <i className="fas fa-chevron-down text-2xl"></i>
        </m.div>
      </section>

      {/* Statistics Section */}
      <section className="statistics-section py-[100px] bg-gradient-to-br from-fastblue via-darkslateblue to-darkpurple2 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <Container className="relative z-10">
          <m.div className="text-center mb-16" {...fadeIn}>
            <h2 className="text-4xl md:text-3xl font-bold mb-4">Join Thousands of Migrants</h2>
            <p className="text-xl text-gray-300">Building communities across the globe</p>
          </m.div>
          <Row className="text-center">
            <Col lg={3} md={6} className="mb-8 md:mb-6">
              <AnimatedCounter end={10000} suffix="+" className="block mb-2" />
              <p className="text-lg text-gray-300">Active Users</p>
            </Col>
            <Col lg={3} md={6} className="mb-8 md:mb-6">
              <AnimatedCounter end={50} suffix="+" className="block mb-2" />
              <p className="text-lg text-gray-300">Countries</p>
            </Col>
            <Col lg={3} md={6} className="mb-8 md:mb-6">
              <AnimatedCounter end={5000} suffix="+" className="block mb-2" />
              <p className="text-lg text-gray-300">Businesses</p>
            </Col>
            <Col lg={3} md={6} className="mb-8 md:mb-6">
              <AnimatedCounter end={1000} suffix="+" className="block mb-2" />
              <p className="text-lg text-gray-300">Events Monthly</p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Features Section with 3D Cards */}
      <section className="features-section py-[120px] md:py-[80px] bg-lightgray relative">
        <Container>
          <m.div className="section-header text-center mb-20" {...fadeIn}>
            <span className="inline-block text-sm font-semibold uppercase tracking-wider text-fastblue mb-4">Features</span>
            <h2 className="text-4xl md:text-3xl font-bold mb-4 text-darkgray">{t('home.features.title')}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('home.features.subtitle')}</p>
          </m.div>
          
          <Row>
            <Col lg={4} md={6} className="mb-8">
              <AnimatedCard delay={0.1}>
                <div className="feature-icon-wrapper mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-fastblue to-neonorange flex items-center justify-center text-white text-2xl shadow-lg">
                    <i className="fas fa-comments"></i>
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-darkgray">{t('home.features.qa.title')}</h3>
                <p className="text-gray-600 leading-relaxed">{t('home.features.qa.description')}</p>
              </AnimatedCard>
            </Col>
            
            <Col lg={4} md={6} className="mb-8">
              <AnimatedCard delay={0.2}>
                <div className="feature-icon-wrapper mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-neonorange to-fastblue flex items-center justify-center text-white text-2xl shadow-lg">
                    <i className="fas fa-calculator"></i>
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-darkgray">{t('home.features.calculator.title')}</h3>
                <p className="text-gray-600 leading-relaxed">{t('home.features.calculator.description')}</p>
              </AnimatedCard>
            </Col>
            
            <Col lg={4} md={6} className="mb-8">
              <AnimatedCard delay={0.3}>
                <div className="feature-icon-wrapper mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-fastblue to-neonorange flex items-center justify-center text-white text-2xl shadow-lg">
                    <i className="fas fa-store"></i>
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-darkgray">{t('home.features.businesses.title')}</h3>
                <p className="text-gray-600 leading-relaxed">{t('home.features.businesses.description')}</p>
              </AnimatedCard>
            </Col>
            
            <Col lg={4} md={6} className="mb-8">
              <AnimatedCard delay={0.4}>
                <div className="feature-icon-wrapper mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-neonorange to-fastblue flex items-center justify-center text-white text-2xl shadow-lg">
                    <i className="fas fa-calendar-alt"></i>
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-darkgray">{t('home.features.events.title')}</h3>
                <p className="text-gray-600 leading-relaxed">{t('home.features.events.description')}</p>
              </AnimatedCard>
            </Col>
            
            <Col lg={4} md={6} className="mb-8">
              <AnimatedCard delay={0.5}>
                <div className="feature-icon-wrapper mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-fastblue to-neonorange flex items-center justify-center text-white text-2xl shadow-lg">
                    <i className="fas fa-shopping-cart"></i>
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-darkgray">{t('home.features.marketplace.title')}</h3>
                <p className="text-gray-600 leading-relaxed">{t('home.features.marketplace.description')}</p>
              </AnimatedCard>
            </Col>
            
            <Col lg={4} md={6} className="mb-8">
              <AnimatedCard delay={0.6}>
                <div className="feature-icon-wrapper mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-neonorange to-fastblue flex items-center justify-center text-white text-2xl shadow-lg">
                    <i className="fas fa-users"></i>
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-darkgray">{t('home.features.collaboration.title')}</h3>
                <p className="text-gray-600 leading-relaxed">{t('home.features.collaboration.description')}</p>
              </AnimatedCard>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Global Nationality Filter */}
      <section className="nationality-filter-section py-[60px] md:py-[40px] bg-gradient-to-b from-white to-lightgray relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(45deg, currentColor 1px, transparent 1px), linear-gradient(-45deg, currentColor 1px, transparent 1px)',
            backgroundSize: '30px 30px'
          }}></div>
        </div>
        <Container className="relative z-10">
          <m.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <NationalityFilter
              nationalities={nationalities}
              selectedNationality={selectedNationality}
              onNationalityChange={setSelectedNationality}
            />
          </m.div>
        </Container>
      </section>

      {/* Post Type Sections with Enhanced Animations */}
      {postTypeSections.map((section, index) => (
        <section 
          key={section.type} 
          className={`post-type-section py-[100px] md:py-[60px] relative overflow-hidden ${index % 2 === 0 ? 'bg-white' : 'bg-lightgray'}`}
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
              backgroundSize: '20px 20px'
            }}></div>
          </div>
          
          <Container className="relative z-10">
            <m.div 
              className={`section-header rounded-2xl p-8 mb-12 text-white relative overflow-hidden ${section.color} shadow-2xl`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent"></div>
              <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <h2 className="text-3xl md:text-2xl font-bold mb-2">{section.title}</h2>
                  <p className="text-lg md:text-base opacity-90">{section.description}</p>
                </div>
                <m.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link 
                    to={section.link} 
                    className="inline-flex items-center gap-2 bg-white text-darkgray px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
                  >
                    {t('home.sections.latestArticles.viewAll')} 
                    <i className="fas fa-arrow-right"></i>
                  </Link>
                </m.div>
              </div>
            </m.div>
            
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block w-16 h-16 border-4 border-fastblue border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-600">{t('home.loading')}</p>
              </div>
            ) : error ? (
              <div className="alert alert-danger rounded-lg p-4" role="alert">
                {error}
              </div>
            ) : (
              <Row className="imigo-post-grid g-3">
                {section.data.length > 0 ? (
                  section.data.map((post, postIndex) => (
                    <Col key={post.id} xs={6} md={4} lg={3} className="mb-1">
                      <m.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ duration: 0.5, delay: postIndex * 0.1 }}
                      >
                        <PostCard post={post} postType={section.type} routeSegment={
                          { Article: 'articles', Business: 'businesses', Event: 'events', Marketplace: 'marketplace' }[section.type]
                        } />
                      </m.div>
                    </Col>
                  ))
                ) : (
                  <Col xs={12}>
                    <m.div 
                      className="text-center py-12"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <p className="text-gray-600 text-lg">{t('home.noContent', { type: section.type.toLowerCase() })}</p>
                    </m.div>
                  </Col>
                )}
              </Row>
            )}
          </Container>
        </section>
      ))}

      {/* App Download Section with 3D Effects */}
      <section className="app-download-section py-[120px] md:py-[80px] bg-gradient-to-br from-fastblue via-darkslateblue to-darkpurple2 text-white relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div className="absolute top-20 left-20 w-64 h-64 bg-neonorange rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-fastblue rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>
        </div>
        
        <Container className="relative z-10">
          <Row className="items-center">
            <Col lg={7} md={12} className="mb-10 lg:mb-0">
              <m.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <span className="inline-block text-sm font-semibold uppercase tracking-wider text-neonorange mb-4">Mobile App</span>
                <h2 className="text-4xl md:text-3xl font-bold mb-6">{t('home.appDownload.title')}</h2>
                <p className="text-xl md:text-lg mb-8 text-gray-300 leading-relaxed">
                  {t('home.appDownload.description')}
                </p>
                <ul className="mb-8 space-y-4">
                  {[
                    t('home.appDownload.features.forum'),
                    t('home.appDownload.features.messaging'),
                    t('home.appDownload.features.notifications'),
                    t('home.appDownload.features.recommendations')
                  ].map((feature, idx) => (
                    <m.li 
                      key={idx}
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: idx * 0.1 }}
                    >
                      <div className="w-6 h-6 rounded-full bg-neonorange flex items-center justify-center flex-shrink-0">
                        <i className="fas fa-check text-white text-xs"></i>
                      </div>
                      <span className="text-lg">{feature}</span>
                    </m.li>
                  ))}
                </ul>
                <div className="app-download-buttons flex flex-wrap gap-4">
                  <m.a 
                    href="https://apps.apple.com/us/app/imigo-connect/id6744267446" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05, y: -5 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <img src={appleStore} alt={t('common.onAppStore')} className="h-[60px] md:h-[50px] drop-shadow-2xl" />
                  </m.a>
                  <m.a 
                    href="https://play.google.com/store/apps/details?id=app.imigo" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05, y: -5 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <img src={googlePlay} alt={t('common.onGooglePlay')} className="h-[60px] md:h-[50px] drop-shadow-2xl" />
                  </m.a>
                </div>
              </m.div>
            </Col>
            <Col lg={5} md={12} className="text-center">
              <m.div
                initial={{ opacity: 0, x: 50, rotateY: -15 }}
                whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                whileHover={{ 
                  scale: 1.05,
                  rotateY: 5,
                  transition: { duration: 0.3 }
                }}
                style={{ transformStyle: 'preserve-3d' }}
              >
                <img 
                  src={appScreenshot} 
                  alt="Imigo App" 
                  className="max-w-[350px] md:max-w-[300px] mx-auto drop-shadow-2xl rounded-3xl border-4 border-white/20" 
                />
              </m.div>
            </Col>
          </Row>
        </Container>
      </section>

    </div>
  )
}

export default HomePage 